import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { CLUBHOUSE_BASE, resolveEntry } from './clubhouse';
import { ClubHouseMoveBanner, captureLegacyArrival } from './components/portal/ClubHouseMoveBanner';

// Critical path - loaded eagerly (login, layout, module routing)
import Layout from './components/Layout';
import Login from './pages/Login';
import ModuleSelector from './pages/ModuleSelector';
import GolfModule from './pages/GolfModule';

// Client portal - loaded eagerly (public landing page)
import ClientPortal from './pages/ClientPortal';
import PortesOuvertes from './pages/PortesOuvertes';
import PortesOuvertesCancel from './pages/PortesOuvertesCancel';
import PublicRegistrationManage from './pages/PublicRegistrationManage';
import GuestBooking from './pages/GuestBooking';
import ResultsBossView from './pages/ResultsBossView';

// Resort website - lazy loaded (independent module)
const ResortApp = lazy(() => import('./pages/resort/ResortApp'));

// All other pages - lazy loaded (code splitting)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Zones = lazy(() => import('./pages/Zones'));
const Products = lazy(() => import('./pages/Products'));
const Employees = lazy(() => import('./pages/Employees'));
const Operations = lazy(() => import('./pages/Operations'));
const Machines = lazy(() => import('./pages/Machines'));
const Applications = lazy(() => import('./pages/Applications'));
const TimeStandards = lazy(() => import('./pages/TimeStandards'));
const Water = lazy(() => import('./pages/Water'));
const Agronomic = lazy(() => import('./pages/Agronomic'));
const ExpensesBudget = lazy(() => import('./pages/ExpensesBudget'));
const Planning = lazy(() => import('./pages/Planning'));
const Tasks = lazy(() => import('./pages/Tasks'));
const TasksAndSurfaces = lazy(() => import('./pages/TasksAndSurfaces'));
const AlexPage = lazy(() => import('./pages/AlexPage'));
const CatalogProducts = lazy(() => import('./pages/CatalogProducts'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const EmergencyReset = lazy(() => import('./pages/EmergencyReset'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const GolfPlanning = lazy(() => import('./pages/GolfPlanning'));
const GolfTeeTimes = lazy(() => import('./pages/GolfTeeTimes'));
const CashierPOS = lazy(() => import('./pages/pos/CashierPOS'));
const ProshopCatalog = lazy(() => import('./pages/pos/ProshopCatalog'));
const PosLayout = lazy(() => import('./pages/pos/PosLayout'));
const GolfTarifs = lazy(() => import('./pages/GolfTarifs'));
const ClientAccounts = lazy(() => import('./pages/pos/ClientAccounts'));
const GiftCards = lazy(() => import('./pages/pos/GiftCards'));
const SepaDebitsPage = lazy(() => import('./pages/pos/SepaDebitsPage'));
const PortalMyAccount = lazy(() => import('./pages/portal/PortalMyAccount'));
const PublicGiftReveal = lazy(() => import('./pages/public/PublicGiftReveal'));
const PracticeMachineSimulator = lazy(() => import('./pages/PracticeMachineSimulator'));
const GolfCours = lazy(() => import('./pages/GolfCours'));
const GolfCompetitions = lazy(() => import('./pages/GolfCompetitions'));
const GolfStages = lazy(() => import('./pages/GolfStages'));
const GolfClients = lazy(() => import('./pages/GolfClients'));
const ClientTypesOverview = lazy(() => import('./pages/ClientTypesOverview'));
const CotisationCalls = lazy(() => import('./pages/CotisationCalls'));
const GolfBudget = lazy(() => import('./pages/GolfBudget'));
const GolfStats = lazy(() => import('./pages/GolfStats'));
const GolfNews = lazy(() => import('./pages/GolfNews'));
const GolfActivityFeed = lazy(() => import('./pages/GolfActivityFeed'));
const GolfAIAdvisor = lazy(() => import('./pages/GolfAIAdvisor'));
const GolfTaylorMade = lazy(() => import('./pages/GolfTaylorMade'));
const GolfGreenFees = lazy(() => import('./pages/GolfGreenFees'));
const GolfRestaurantReservations = lazy(() => import('./pages/GolfRestaurantReservations'));
const GolfExpenses = lazy(() => import('./pages/GolfExpenses'));
const GolfSEO = lazy(() => import('./pages/GolfSEO'));
const GolfMewsSettings = lazy(() => import('./pages/GolfMewsSettings'));
const RestaurantModule = lazy(() => import('./pages/RestaurantModule'));
const AllGolfs = lazy(() => import('./pages/AllGolfs'));
const GolfDashboard = lazy(() => import('./pages/GolfDashboard'));
const GolfPortalSettings = lazy(() => import('./pages/GolfPortalSettings'));
const GolfCourseConfig = lazy(() => import('./pages/GolfCourseConfig'));
const GolfSchoolAdmin = lazy(() => import('./pages/GolfSchoolAdmin'));
const EmailManager = lazy(() => import('./pages/EmailManager'));
const GolfNotifications = lazy(() => import('./pages/GolfNotifications'));
const GolfPayments = lazy(() => import('./pages/GolfPayments'));
const GolfPackAnomalies = lazy(() => import('./pages/GolfPackAnomalies'));
const AdminControlCenter = lazy(() => import('./pages/AdminControlCenter'));
const GolfCoursePaymentsReport = lazy(() => import('./pages/GolfCoursePaymentsReport'));
const GolfProTasks = lazy(() => import('./pages/GolfProTasks'));
const ResultsPage = lazy(() => import('./pages/ResultsPage'));
const Communication = lazy(() => import('./pages/Communication'));
const GolfDevis = lazy(() => import('./pages/GolfDevis'));
const OrganisationCatalog = lazy(() => import('./pages/OrganisationCatalog'));
const ResortCMS = lazy(() => import('./pages/ResortCMS'));
const MaintenanceSettings = lazy(() => import('./pages/MaintenanceSettings'));
const GolfBunkerMaintenance = lazy(() => import('./pages/GolfBunkerMaintenance'));
const BunkerMobileWorker = lazy(() => import('./pages/BunkerMobileWorker'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const PublicEventRegistration = lazy(() => import('./pages/PublicEventRegistration'));
const PublicEventsList = lazy(() => import('./pages/PublicEventsList'));
const KempferhofApp = lazy(() => import('./pages/kempferhof/KempferhofApp'));
const KempferhofInvite = lazy(() => import('./pages/kempferhof/KempferhofInvite'));
const KempferhofTarifs = lazy(() => import('./pages/kempferhof/KempferhofTarifs'));
const KempferhofQuestionnaire = lazy(() => import('./pages/kempferhof/KempferhofQuestionnaire'));
const KempferhofQuestionnaireEditor = lazy(() => import('./pages/kempferhof/KempferhofPresetsManager'));
const KempferhofIntroEditor = lazy(() => import('./pages/kempferhof/KempferhofIntroEditor'));
const KempferhofTarifsPublic = lazy(() => import('./pages/kempferhof/KempferhofTarifsPublic'));
const KempferhofInviteAnswers = lazy(() => import('./pages/kempferhof/KempferhofInviteAnswers'));
const KempferhofBaremes = lazy(() => import('./pages/kempferhof/KempferhofBaremes'));
const KempferhofQuestionnairesPage = lazy(() => import('./pages/kempferhof/KempferhofQuestionnairesPage'));
const KempferhofViewerPage = lazy(() => import('./pages/kempferhof/KempferhofViewerPage'));
const KempferhofAfterwork = lazy(() => import('./pages/kempferhof/KempferhofAfterwork'));
const AfterworkPublicForm = lazy(() => import('./pages/kempferhof/AfterworkPublicForm'));

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center h-[60vh]">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
  </div>
);

const isAdminAccount = (u) => !!(u?.is_super_admin || u?.user_role === 'admin' || u?.role === 'admin' || u?.role === 'super_admin'
  || (u?.email || '').toLowerCase() === 'aa@bassmanager.com');

const ProtectedRoute = ({ children, module, adminOnly }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdminAccount(user)) return <Navigate to="/modules" replace />;

  // Vérification stricte du module si demandé : redirige vers le sélecteur de modules
  // si l'utilisateur n'a pas ce module dans user.modules (aucun bypass de rôle).
  if (module) {
    const userModules = user?.modules || [];
    if (!userModules.includes(module)) {
      return <Navigate to="/modules" replace />;
    }
  }

  return children;
};

// Redirige /portail vers / en conservant les query params
const PortailRedirect = () => {
  const search = window.location.search;
  return <Navigate to={`/${search}`} replace />;
};


// Route qui redirige selon les modules de l'utilisateur
const ModuleRedirect = () => {
  const { user } = useAuth();
  
  // Super admin → rediriger vers la page "Tous les golfs"
  if (user?.is_super_admin) {
    return <Navigate to="/all-golfs" replace />;
  }
  
  // Si l'utilisateur a un module par défaut, rediriger directement
  if (user?.default_module) {
    const moduleRoutes = {
      'entretien': '/entretien',
      'golf': '/golf',
      'restaurant': '/restaurant',
      'kempferhof': '/kempferhof'
    };
    return <Navigate to={moduleRoutes[user.default_module] || '/modules'} replace />;
  }
  
  // Si l'utilisateur n'a qu'un seul module, rediriger directement
  if (user?.modules?.length === 1) {
    const moduleRoutes = {
      'entretien': '/entretien',
      'golf': '/golf',
      'restaurant': '/restaurant',
      'kempferhof': '/kempferhof'
    };
    return <Navigate to={moduleRoutes[user.modules[0]] || '/modules'} replace />;
  }
  
  // Sinon, afficher le sélecteur de modules
  return <Navigate to="/modules" replace />;
};

// ====================================================================
// Routage unifié : vitrine à la racine du domaine, Club House sous /clubhouse.
// Les anciennes adresses (/portail, /login, /golf/planning, /r/XYZ…) sont
// réécrites vers /clubhouse/… sans rechargement ; /resort/* vers la racine.
// ====================================================================
const ENTRY = (() => {
  const entry = resolveEntry();
  // Ancien raccourci « vitrine » lancé en mode appli (iOS < 16.4 ignore start_url) : on ouvre le Club House
  const launchedAsApp = (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true) && !document.referrer;
  if (entry.mode === 'vitrine' && launchedAsApp) return { mode: 'clubhouse', rewrite: CLUBHOUSE_BASE };
  return entry;
})();
if (ENTRY.external) {
  window.location.replace(ENTRY.external);
} else if (ENTRY.rewrite) {
  window.history.replaceState(window.history.state, '', ENTRY.rewrite);
}
captureLegacyArrival();

// Le Club House ne doit pas être indexé (la vitrine garde son SEO)
function ClubHouseNoIndex() {
  useEffect(() => {
    const meta = document.querySelector('meta[name="robots"]');
    const previous = meta?.getAttribute('content');
    meta?.setAttribute('content', 'noindex, nofollow');
    return () => { if (meta && previous) meta.setAttribute('content', previous); };
  }, []);
  return null;
}

const AppRoutes = () => {
  const { user } = useAuth();

  if (ENTRY.external) return <PageLoader />;

  if (ENTRY.mode === 'vitrine') {
    return (
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Site vitrine monté à la racine (kempferhof.fr/, /golf, /hotel, etc.) */}
            <Route path="/*" element={<ResortApp />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter basename={CLUBHOUSE_BASE}>
      <ClubHouseNoIndex />
      <ClubHouseMoveBanner />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Page d'accueil = Portail Client */}
        <Route path="/" element={<ClientPortal />} />
        <Route path="/portal" element={<Navigate to="/" replace />} />
        <Route path="/portail" element={<PortailRedirect />} />
        <Route path="/portail/mon-compte" element={<PortalMyAccount />} />
        <Route path="/client" element={<Navigate to="/" replace />} />
        
        {/* Portes Ouvertes - route publique pour les non-golfeurs */}
        <Route path="/portes-ouvertes" element={<PortesOuvertes />} />
        <Route path="/portes-ouvertes/annuler/:token" element={<PortesOuvertesCancel />} />
        
        {/* Gestion publique d'une inscription (compétition/afterwork/PO) via manage_token (iter228) */}
        <Route path="/reservation/gerer/:token" element={<PublicRegistrationManage />} />
        
        {/* Inscription publique aux événements (compétitions, ateliers, afterworks) via lien Insta/QR */}
        <Route path="/inscription/event/:eventId" element={<PublicEventRegistration />} />
        <Route path="/evenements" element={<PublicEventsList />} />
        <Route path="/invites" element={<GuestBooking />} />
        
        {/* Vue Boss résultats - route publique avec token */}
        <Route path="/resultats-boss" element={<ResultsBossView />} />
        <Route path="/r/:code" element={<ResultsBossView />} />

        {/* Cadeau magique : page publique animée d'ouverture de bon cadeau */}
        <Route path="/cadeau/:token" element={<PublicGiftReveal />} />

        {/* Simulateur de machine à balles — page publique de test (QR Practice) */}
        <Route path="/practice/machine-simulator" element={<PracticeMachineSimulator />} />
        
        {/* Login → redirige vers portail (porte d'entrée unique) */}
        <Route path="/login" element={user ? <Navigate to="/modules" replace /> : <Navigate to="/portail" replace />} />

        {/* Reset d'urgence du mot de passe admin (public, protégé par code à usage unique) */}
        <Route path="/admin/emergency-reset" element={<EmergencyReset />} />

        {/* Mot de passe oublié (public, pour admins, employés et clients) */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        
        {/* Route modules - page d'accueil pour les salariés connectés */}
        <Route path="/modules" element={
          <ProtectedRoute>
            <ModuleSelector />
          </ProtectedRoute>
        } />
        
        {/* Admin — gestion des utilisateurs (administrateurs uniquement) */}
        <Route path="/admin/users" element={
          <ProtectedRoute adminOnly>
            <AdminUsers />
          </ProtectedRoute>
        } />
        
        {/* Super Admin - Tous les golfs (sans sidebar) */}
        <Route path="/all-golfs" element={
          <ProtectedRoute>
            <AllGolfs />
          </ProtectedRoute>
        } />
        
        {/* Dashboard d'un golf - redirige vers modules */}
        <Route path="/golf-dashboard" element={
          <ProtectedRoute>
            <Navigate to="/modules" replace />
          </ProtectedRoute>
        } />
        
        {/* Module Golf avec sous-routes */}
        <Route path="/golf" element={
          <ProtectedRoute>
            <GolfModule />
          </ProtectedRoute>
        }>
          <Route index element={<GolfPlanning />} />
          <Route path="planning" element={<GolfPlanning />} />
          <Route path="tee-times" element={<GolfTeeTimes />} />
          <Route path="caisse" element={<PosLayout />}>
            <Route index element={<CashierPOS />} />
            <Route path="encaissement" element={<CashierPOS />} />
            <Route path="comptes-clients" element={<ClientAccounts />} />
            <Route path="bons-cadeaux" element={<GiftCards />} />
            <Route path="tarifs" element={<Navigate to="/golf/catalogue" replace />} />
            <Route path="catalogue" element={<Navigate to="/golf/catalogue?view=proshop" replace />} />
            <Route path="prelevements-sepa" element={<SepaDebitsPage />} />
          </Route>
          {/* Legacy routes — redirection vers le hub à onglets */}
          <Route path="comptes-clients" element={<Navigate to="/golf/caisse/comptes-clients" replace />} />
          <Route path="bons-cadeaux" element={<Navigate to="/golf/caisse/bons-cadeaux" replace />} />
          <Route path="cours" element={<GolfCours />} />
          <Route path="competitions" element={<GolfCompetitions />} />
          <Route path="stages" element={<GolfStages />} />
          <Route path="restaurant-reservations" element={<GolfRestaurantReservations />} />
          <Route path="notes-de-frais" element={<GolfExpenses />} />
          <Route path="seo" element={<GolfSEO />} />
          <Route path="mews" element={<GolfMewsSettings />} />
          <Route path="clients" element={<GolfClients />} />
          <Route path="types-clients" element={<ClientTypesOverview />} />
          <Route path="appels-cotisation" element={<CotisationCalls />} />
          <Route path="budget" element={<GolfBudget />} />
          <Route path="stats" element={<GolfStats />} />
          <Route path="conseiller-ia" element={<GolfAIAdvisor />} />
          <Route path="taylor-made" element={<GolfTaylorMade />} />
          <Route path="green-fees" element={<GolfGreenFees />} />
          <Route path="news" element={<GolfNews />} />
          <Route path="activity" element={<GolfActivityFeed />} />
          <Route path="portal-settings" element={<GolfPortalSettings />} />
          <Route path="course-config" element={<GolfCourseConfig />} />
          <Route path="ecole" element={<GolfSchoolAdmin />} />
          <Route path="actualites" element={<GolfPortalSettings />} />
          <Route path="notifications" element={<GolfNotifications />} />
          <Route path="email-management" element={<EmailManager />} />
          <Route path="mailing" element={<GolfNotifications />} />
          <Route path="paiements" element={<GolfPayments />} />
          <Route path="pack-anomalies" element={<GolfPackAnomalies />} />
          <Route path="control-center" element={<AdminControlCenter />} />
          <Route path="rapports/cours-paiements" element={<GolfCoursePaymentsReport />} />
          <Route path="mes-taches" element={<GolfProTasks />} />
          <Route path="resultats" element={<ResultsPage />} />
          <Route path="communication" element={<Communication />} />
          <Route path="devis" element={<GolfDevis />} />
          <Route path="catalogue" element={<OrganisationCatalog />} />
          <Route path="site-vitrine" element={<ResortCMS />} />
          <Route path="audit-log" element={<AuditLog />} />
        </Route>
        
        {/* Module Restaurant */}
        <Route path="/restaurant" element={
          <ProtectedRoute>
            <RestaurantModule />
          </ProtectedRoute>
        } />

        {/* Module Kempferhof — Compte de résultat dynamique */}
        <Route path="/kempferhof" element={
          <ProtectedRoute module="kempferhof">
            <KempferhofApp />
          </ProtectedRoute>
        } />

        {/* Module Kempferhof — Lien d'invitation public (sans auth) */}
        <Route path="/kempferhof/i/:token" element={<KempferhofInvite />} />

        {/* Module Kempferhof — Questionnaire mobile-first public (sans auth) */}
        <Route path="/kempferhof/q/:token" element={<KempferhofQuestionnaire />} />

        {/* Module Kempferhof — Plaquette tarifaire publique (sans auth) */}
        <Route path="/kempferhof/tarifs-public/:token" element={<KempferhofTarifsPublic />} />

        {/* Module Kempferhof — Vue admin des réponses brutes d'un questionnaire */}
        <Route path="/kempferhof/invitations/:id/answers" element={
          <ProtectedRoute module="kempferhof">
            <KempferhofInviteAnswers />
          </ProtectedRoute>
        } />

        {/* Module Kempferhof — Éditeur du template de questionnaire (admin) */}
        <Route path="/kempferhof/questionnaire-editor" element={
          <ProtectedRoute module="kempferhof">
            <KempferhofQuestionnaireEditor />
          </ProtectedRoute>
        } />

        {/* Module Kempferhof — Éditeur de la page d'accueil du questionnaire (admin) */}
        <Route path="/kempferhof/intro-editor" element={
          <ProtectedRoute module="kempferhof">
            <KempferhofIntroEditor />
          </ProtectedRoute>
        } />

        {/* Module Kempferhof — Éditeur des barèmes du diagnostic (admin) */}
        <Route path="/kempferhof/baremes" element={
          <ProtectedRoute module="kempferhof">
            <KempferhofBaremes />
          </ProtectedRoute>
        } />

        {/* Module Kempferhof — Page Questionnaires (gestion des invitations) */}
        <Route path="/kempferhof/questionnaires" element={
          <ProtectedRoute module="kempferhof">
            <KempferhofQuestionnairesPage />
          </ProtectedRoute>
        } />

        {/* Module Kempferhof — Lien magique de consultation (lecture seule, PUBLIC) */}
        <Route path="/kempferhof/viewer/:token" element={<KempferhofViewerPage />} />

        {/* Afterwork Pronostic — page publique (mobile, QR code) */}
        <Route path="/afterwork/:token" element={<AfterworkPublicForm />} />

        {/* Afterwork Pronostic — dashboard admin (accès Golf ou Kempferhof) */}
        <Route path="/kempferhof/afterwork" element={
          <ProtectedRoute>
            <KempferhofAfterwork />
          </ProtectedRoute>
        } />

        {/* Module Kempferhof — Plaquette tarifaire dynamique (admin) */}
        <Route path="/kempferhof/tarifs" element={
          <ProtectedRoute module="kempferhof">
            <KempferhofTarifs />
          </ProtectedRoute>
        } />
        <Route path="/kempferhof/tarifs/:cat" element={
          <ProtectedRoute module="kempferhof">
            <KempferhofTarifs />
          </ProtectedRoute>
        } />
        
        {/* Module Entretien - Dashboard */}
        <Route path="/entretien" element={
          <ProtectedRoute>
            <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/planning" element={
          <ProtectedRoute>
            <Layout><Planning /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/tasks" element={
          <ProtectedRoute>
            <Layout><Tasks /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/tasks-surfaces" element={
          <ProtectedRoute>
            <Layout><TasksAndSurfaces /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/alex" element={
          <ProtectedRoute>
            <Layout><AlexPage /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/zones" element={
          <ProtectedRoute>
            <Layout><Zones /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/products" element={
          <ProtectedRoute>
            <Layout><Products /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/applications" element={
          <ProtectedRoute>
            <Layout><Applications /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/employees" element={
          <ProtectedRoute>
            <Layout><Employees /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/time-standards" element={
          <ProtectedRoute>
            <Layout><TimeStandards /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/calendar" element={
          <ProtectedRoute>
            <Layout><Operations /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/water" element={
          <ProtectedRoute>
            <Layout><Water /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/machines" element={
          <ProtectedRoute>
            <Layout><Machines /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/agronomic" element={
          <ProtectedRoute>
            <Layout><Agronomic /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/expenses-budget" element={
          <ProtectedRoute>
            <Layout><ExpensesBudget /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/catalog-products" element={
          <ProtectedRoute>
            <Layout><CatalogProducts /></Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/maintenance-settings" element={
          <ProtectedRoute>
            <Layout><MaintenanceSettings /></Layout>
          </ProtectedRoute>
        } />

        <Route path="/bunker-maintenance" element={
          <ProtectedRoute>
            <Layout><GolfBunkerMaintenance /></Layout>
          </ProtectedRoute>
        } />

        <Route path="/bunker-mobile" element={
          <ProtectedRoute>
            <BunkerMobileWorker />
          </ProtectedRoute>
        } />
        
        {/* Redirections des anciennes routes */}
        <Route path="/budget" element={<Navigate to="/expenses-budget" replace />} />
        <Route path="/products" element={<Navigate to="/expenses-budget" replace />} />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

function App() {
  const initialVersion = useRef(null);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const checkVersion = async () => {
      try {
        const res = await fetch(`${API_URL}/api/app-version`);
        if (!res.ok) return;
        const data = await res.json();
        if (!initialVersion.current) {
          initialVersion.current = data.version;
        } else if (data.version !== initialVersion.current) {
          // Nouvelle version détectée - forcer le rechargement
          if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const reg of registrations) {
              await reg.update();
            }
          }
          // Vider le cache et recharger
          if ('caches' in window) {
            const names = await caches.keys();
            await Promise.all(names.map(n => caches.delete(n)));
          }
          window.location.reload(true);
        }
      } catch (e) { /* silently fail */ }
    };

    // Vérifier toutes les 2 minutes
    checkVersion();
    const interval = setInterval(checkVersion, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <AuthProvider>
      <AppRoutes />
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
